import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import { buildApiFilterParams } from '@/utils/apiFilterParams';
import stocks from './submodules/stocks';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const EVOLUTION_FILTERS_STORAGE_KEY = getStorageKey('stockstats', 'home-evolution-filters');
const DEFAULT_PERIOD_PRESET = 'last30days';
const DEFAULT_EVOLUTION_FILTERS = {
    period: DEFAULT_PERIOD_PRESET,
};

function buildEvolutionRequestParams(state) {
    const filters = state.evolutionFilters || {};
    const params = buildApiFilterParams(
        {
            start: state.stockEvolutionPeriod?.start,
            end: state.stockEvolutionPeriod?.end,
            scale: state.stockEvolutionPeriod?.scale || 'month',
            stock: filters.stock,
            producttype: filters.producttype,
            establishment: filters.establishment,
            customer: filters.customer,
            product: filters.product,
            products: filters.products,
            search: filters.search,
            ...Object.fromEntries(
                Object.keys(filters)
                    .filter((key) => key.startsWith('metadata__') && filters[key] != null && filters[key] !== '')
                    .map((key) => [key, filters[key]]),
            ),
        },
        { stripPagination: false },
    );
    return params;
}

const state = {
    stock: null,
    producttype: null,
    evolutionFilters: { ...DEFAULT_EVOLUTION_FILTERS },
    stockEvolution: null,
    stockEvolutionSummary: null,
    stockEvolutionPeriod: null,
    stockEvolutionLoading: false,
    stockEvolutionLoadingErrors: {},
    stock_evolution_cancel_source: null,
};

const mutations = {
    updateStockEvolution(state, stockEvolution) {
        state.stockEvolution = stockEvolution;
        state.stockEvolutionLoading = false;
        state.stockEvolutionLoadingErrors = {};
    },
    updateStockEvolutionSummary(state, stockEvolutionSummary) {
        state.stockEvolutionSummary = stockEvolutionSummary;
    },
    updateStockEvolutionPeriod(state, stockEvolutionPeriod) {
        state.stockEvolution = null;
        state.stockEvolutionSummary = null;
        state.stockEvolutionPeriod = stockEvolutionPeriod;
    },
    updateStockEvolutionLoading(state, stockEvolutionLoading) {
        state.stockEvolutionLoading = stockEvolutionLoading;
    },
    updateStockEvolutionLoadingErrors(state, stockEvolutionLoadingErrors) {
        state.stockEvolutionLoading = false;
        state.stockEvolutionLoadingErrors = stockEvolutionLoadingErrors;
    },
    updateStock(state, stock) {
        state.stock = stock;
    },
    updateProducttype(state, producttype) {
        state.producttype = producttype;
    },
    updateEvolutionFilters(state, evolutionFilters) {
        state.evolutionFilters = evolutionFilters;
        state.stock = evolutionFilters.stock || null;
        state.producttype = evolutionFilters.producttype || null;
        saveFiltersToStorage(EVOLUTION_FILTERS_STORAGE_KEY, evolutionFilters);
    },
    updateStockEvolutionCancelSource(state, cancel_source) {
        state.stock_evolution_cancel_source = cancel_source;
    },

    hydrateEvolutionFiltersFromStorage(state) {
        const evolutionFilters = {
            ...DEFAULT_EVOLUTION_FILTERS,
            ...loadFiltersFromStorage(
                EVOLUTION_FILTERS_STORAGE_KEY,
                DEFAULT_EVOLUTION_FILTERS,
            ),
        };
        if (!evolutionFilters.period) {
            evolutionFilters.period = DEFAULT_PERIOD_PRESET;
        }
        state.evolutionFilters = evolutionFilters;
        state.stock = evolutionFilters.stock || null;
        state.producttype = evolutionFilters.producttype || null;
    },
};

const actions = {
    hydrateOrgStorageSettings({ commit }) {
        commit('hydrateEvolutionFiltersFromStorage');
    },

    async fetchStockEvolution({ commit, state }) {
        commit('updateStockEvolutionLoading', true);

        if (state.stock_evolution_cancel_source) {
            state.stock_evolution_cancel_source.cancel('canceled');
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateStockEvolutionCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/stats/stock/evolution/', {
                params: buildEvolutionRequestParams(state),
                cancelToken: state.stock_evolution_cancel_source?.token,
            });
            const { summary, ...chartData } = response.data;
            commit('updateStockEvolution', chartData);
            commit('updateStockEvolutionSummary', summary || null);
            commit('updateStockEvolutionCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateStockEvolutionLoadingErrors', error.details);
            throw error;
        }
    },

    setStockEvolutionPeriod({ commit, dispatch }, params) {
        commit('updateStockEvolutionPeriod', params);
        dispatch('fetchStockEvolution');
    },

    changeStock({ commit, dispatch, state }, stock) {
        const evolutionFilters = {
            ...state.evolutionFilters,
            stock: stock || null,
        };
        commit('updateEvolutionFilters', evolutionFilters);
        dispatch('fetchStockEvolution');
    },

    changeProducttype({ commit, dispatch, state }, producttype) {
        const evolutionFilters = {
            ...state.evolutionFilters,
            producttype: producttype || null,
        };
        Object.keys(evolutionFilters).forEach((key) => {
            if (key.startsWith('metadata__')) {
                delete evolutionFilters[key];
            }
        });
        commit('updateEvolutionFilters', evolutionFilters);
        dispatch('fetchStockEvolution');
    },

    changeEvolutionFilters({ commit, dispatch, state }, filters) {
        const nextFilters = {
            ...filters,
            period: filters.period || DEFAULT_PERIOD_PRESET,
        };
        if (nextFilters.producttype !== state.evolutionFilters?.producttype) {
            Object.keys(nextFilters).forEach((key) => {
                if (key.startsWith('metadata__')) {
                    delete nextFilters[key];
                }
            });
        }
        commit('updateEvolutionFilters', nextFilters);
        dispatch('fetchStockEvolution');
    },

    init({ commit, state, dispatch }) {
        commit('updateStock', state.evolutionFilters.stock || null);
        commit('updateProducttype', state.evolutionFilters.producttype || null);
        dispatch('stocks/init', { filters: {} });
        dispatch('stocks/fetchStocks');
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        stocks: stocks(),
    },
};
